import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import {
  Clock,
  Bell,
  Shield,
  ClipboardList,
  ClipboardCheck,
  FolderOpen,
  HelpCircle,
  Settings,
  LogOut,
  Lock,
  ChevronRight,
  Calendar,
} from 'lucide-react';

const MobileMore = () => {
  const navigate = useNavigate();
  const { operator, logout, hasFeature, isWorker, isTimesheetOnly, can } = useMobileAuth();

  const timesheetOnly = isTimesheetOnly();

  const handleLogout = () => {
    logout();
    navigate('/mobile/login');
  };

  const menuItems = [
    {
      icon: Clock,
      label: 'Timesheets',
      path: '/mobile/timesheet',
      visible: (isWorker() || can('timesheets.read')) && hasFeature('timesheets'),
      locked: (isWorker() || can('timesheets.read')) && !hasFeature('timesheets'),
      testId: 'more-timesheets',
    },
    {
      icon: ClipboardList,
      label: 'Forms',
      path: '/mobile/forms',
      visible: (isWorker() || can('forms.read')) && hasFeature('forms') && !timesheetOnly,
      locked: (isWorker() || can('forms.read')) && !hasFeature('forms') && !timesheetOnly,
      testId: 'more-forms',
    },
    {
      icon: Bell,
      label: 'Notifications',
      path: '/mobile/notifications',
      visible: true,
      testId: 'more-notifications',
    },
    {
      icon: Shield,
      label: 'Compliance',
      path: '/mobile/compliance',
      visible: (isWorker() || can('compliance.read')) && hasFeature('compliance') && !timesheetOnly,
      locked: (isWorker() || can('compliance.read')) && !hasFeature('compliance') && !timesheetOnly,
      testId: 'more-compliance',
    },
    {
      icon: FolderOpen,
      label: 'Documents',
      path: '/mobile/documents',
      visible: (isWorker() || can('documents.read')) && !timesheetOnly,
      testId: 'more-documents',
    },
    {
      icon: ClipboardCheck,
      label: 'Dry-Hire Inspections',
      path: '/mobile/inspections',
      visible: operator?.is_yard_dispatcher,
      testId: 'more-inspections',
    },
    {
      icon: HelpCircle,
      label: 'Help',
      path: '/mobile/help',
      visible: true,
      testId: 'more-help',
    },
    {
      icon: Settings,
      label: 'Preferences',
      path: '/mobile/settings',
      visible: true,
      testId: 'more-preferences',
    },
    {
      icon: Calendar,
      label: 'My Leave',
      path: '/mobile/leave',
      visible: isWorker() && hasFeature('worker_availability'),
      testId: 'more-leave',
    },
  ];

  return (
    <MobileLayout>
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center justify-between">
          <h1 className="text-[var(--m-text)] font-bold text-lg">More</h1>
        </div>
      </div>

      <div className="p-4 space-y-1">
        {menuItems.map((item) => {
          if (!item.visible && !item.locked) return null;
          const Icon = item.icon;
          const disabled = item.locked && !item.visible;

          return (
            <button
              key={item.path}
              onClick={() => !disabled && navigate(item.path)}
              disabled={disabled}
              className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-lg transition-colors ${
                disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-[var(--m-bg-elevated)] active:bg-[var(--m-bg-elevated)]'
              }`}
              data-testid={item.testId}
            >
              <div className="relative">
                <Icon className="h-5 w-5 text-[var(--m-text-body)]" />
                {disabled && (
                  <Lock className="h-3 w-3 absolute -bottom-1 -right-1 text-[var(--m-text-muted)]" />
                )}
              </div>
              <span className={`flex-1 text-left text-sm ${disabled ? 'text-[var(--m-text-muted)]' : 'text-[var(--m-text)]'}`}>
                {item.label}
              </span>
              {item.badge && (
                <span className="min-w-[20px] h-[20px] bg-[#ffd108] text-[#1a1a1a] text-xs font-bold rounded-full flex items-center justify-center px-1">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
              {!disabled && <ChevronRight className="h-4 w-4 text-[var(--m-text-muted)]" />}
            </button>
          );
        })}

        {/* Logout */}
        <div className="pt-4 border-t border-[var(--m-border)] mt-4">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-4 px-4 py-3.5 rounded-lg hover:bg-red-500/10 active:bg-red-500/20 transition-colors"
            data-testid="more-logout"
          >
            <LogOut className="h-5 w-5 text-red-400" />
            <span className="flex-1 text-left text-sm text-red-400">Log Out</span>
          </button>
        </div>
      </div>
    </MobileLayout>
  );
};

export default MobileMore;
